import {
  BookOpen, CheckCircle2, ClipboardCheck, Compass, Eye, FileText, GraduationCap, History,
  Hourglass, Inbox, LayoutGrid, LayoutTemplate, Layers, ListChecks, Mail, MailPlus,
  MessageSquare, MessageSquarePlus, Save, Send, ShieldCheck, Sparkles, Tag, Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type Seite = 'unten' | 'oben' | 'rechts' | 'links';

/** Wer einen Schritt sieht. Ohne Angabe: alle. */
export type TourRolle = 'vorbereiten' | 'einreicher' | 'team' | 'redaktion' | 'admin';

export type TourRechte = Record<TourRolle, boolean>;

export type TourSchritt = {
  id: string;
  Icon: LucideIcon;
  titel: string;
  text: string;
  /** Wert des data-tour-Attributs, das hervorgehoben wird. Ohne Angabe steht die Karte mittig. */
  ziel?: string;
  rolle?: TourRolle;
  /**
   * Adresse des Schritts. Im Rundgang die Seite, auf der er spielt. In einer
   * Seiten-Tour ein Reiter derselben Seite, z. B. „/freigaben?reiter=protokoll"
   * — dorthin schaltet die Tour vor dem Schritt selbst um.
   */
  seite?: string;
  bevorzugt?: Seite[];
};

const RECHTS: Seite[] = ['rechts', 'unten', 'oben', 'links'];
const LINKS: Seite[] = ['links', 'unten', 'oben', 'rechts'];
const UNTEN: Seite[] = ['unten', 'oben', 'rechts', 'links'];

/**
 * Alle Touren — wie im Event Manager.
 *
 * „einfuehrung" ist der Rundgang durch das Mailtool und beginnt auf der
 * Startseite. Alle anderen sind Klick-Touren und bleiben auf ihrer Seite — sie
 * starten aus dem Erklärkasten oben über „Zeig mir die Seite".
 *
 * Fehlt ein Ziel (etwa die Vorschau, solange keine Vorlage gewählt ist),
 * steht die Karte mittig — deshalb erklärt jeder Text auch ohne
 * hervorgehobenes Element, worum es geht. Jedes „ziel" muss als
 * data-tour-Attribut irgendwo in src vorkommen.
 */
export const TOUREN: Record<string, TourSchritt[]> = {
  // ---------------------------------------------------------------- Rundgang
  einfuehrung: [
    {
      id: 'willkommen',
      Icon: Compass,
      titel: 'Kurzer Rundgang',
      text: 'Zwei Minuten durch das Mailtool: wo Du was findest und wie eine Mail von der Idee bis in die Postfächer läuft. Mit den Pfeiltasten kommst Du vor und zurück, mit Escape steigst Du jederzeit aus.',
      seite: '/',
    },
    {
      id: 'antrag',
      Icon: ShieldCheck,
      titel: 'Eine Mail ist erst mal ein Vorschlag',
      text: 'Das Wichtigste vorweg: Was Du hier vorbereitest, geht nicht von selbst raus. Du stellst die Mail zusammen und reichst sie ein. Die Redaktion prüft Sprache, Form und Empfänger, fragt bei Bedarf nach und gibt frei — erst dann geht sie zu Deinem Wunschtermin an die Empfänger.',
      rolle: 'einreicher',
    },
    {
      id: 'antrag-team',
      Icon: ShieldCheck,
      titel: 'Neu: Mails von Geschäftsführung und Studioleitung',
      text: 'Geschäftsführung und Studioleitung bereiten eigene Mails im Assistenten vor und reichen sie ein. Die Redaktion prüft und gibt frei — erst dann entsteht der Brevo-Entwurf und die Mail wird verschickt.',
      rolle: 'team',
    },
    {
      id: 'seitenleiste',
      Icon: LayoutGrid,
      titel: 'Links steht alles, was Du erreichen kannst',
      text: 'Die Seitenleiste ist das Inhaltsverzeichnis, nach Themen gruppiert — wie im CRM. Du siehst nur die Punkte, die zu Deiner Rolle gehören.',
      ziel: 'seitenleiste',
      bevorzugt: RECHTS,
    },
    {
      id: 'neue-mail',
      Icon: MailPlus,
      titel: 'Hier entsteht eine Mail',
      text: 'Der Assistent führt Dich in fünf Schritten durch: Vorlage wählen, Fragen vorab beantworten, Inhalt ausfüllen, Empfänger wählen, prüfen und einreichen. HTML brauchst Du nicht — die Mail sieht aus wie alle Randori-Mails.',
      ziel: 'nav-neue-mail',
      rolle: 'vorbereiten',
      bevorzugt: RECHTS,
    },
    {
      id: 'meine-mails',
      Icon: Inbox,
      titel: 'Meine Mails zeigt, wo alles steht',
      text: 'Jede Mail, die Du vorbereitet hast, mit ihrem Stand: Entwurf, eingereicht, in Prüfung, Rückfrage, geplant oder versendet. Dort machst Du an Entwürfen weiter und beantwortest Rückfragen.',
      ziel: 'nav-meine-mails',
      rolle: 'vorbereiten',
      bevorzugt: RECHTS,
    },
    {
      id: 'vorlagen',
      Icon: LayoutTemplate,
      titel: 'Die Vorlagen',
      text: 'Acht Vorlagen, nachgebaut aus den Mails der letzten Monate — vom ausfallenden Training über den neuen Stundenplan bis zur Gürtelprüfung. Dort siehst Du vorab, wie die fertige Mail aussieht.',
      ziel: 'nav-vorlagen',
      bevorzugt: RECHTS,
    },
    {
      id: 'freigaben',
      Icon: ClipboardCheck,
      titel: 'Unter Freigaben entscheidet die Redaktion',
      text: '„Offen" ist die Arbeitsliste mit allem, was eingereicht ist — mit Wunschtermin und den Antworten auf die Vorab-Fragen. „Entschieden" und „Protokoll" zeigen, wer wann was entschieden hat. Die Zahl am Menüpunkt sagt, wie viel gerade wartet.',
      ziel: 'nav-freigaben',
      rolle: 'redaktion',
      bevorzugt: RECHTS,
    },
    {
      id: 'kampagnen',
      Icon: Mail,
      titel: 'Kampagnen wie bisher',
      text: 'Der freie Kampagnen-Editor bleibt für Newsletter und alles Größere. Mails aus dem Assistenten erscheinen hier ebenfalls, mit ihrem Freigabe-Stand.',
      ziel: 'nav-kampagnen',
      rolle: 'team',
      bevorzugt: RECHTS,
    },
    {
      id: 'gruppen',
      Icon: Tag,
      titel: 'Gruppen — auch aus CRM-Tags',
      text: 'Neben den Gruppen je Sportschule gibt es Gruppen aus CRM-Tags („CRM-Tag: …"): Wer im CRM den Tag trägt, ist drin — stündlich aktualisiert. Damit schickt die Studioleitung Tag-Mails, ohne Adressen anzufassen.',
      ziel: 'nav-gruppen',
      rolle: 'team',
      bevorzugt: RECHTS,
    },
    {
      id: 'handbuch',
      Icon: BookOpen,
      titel: "So funktioniert's",
      text: 'Der ganze Ablauf zum Nachlesen: wer wann dran ist, wie viel Vorlauf die Redaktion braucht, wer die Mail bekommt und die häufigsten Fragen.',
      ziel: 'nav-handbuch',
      bevorzugt: RECHTS,
    },
    {
      id: 'doktorhut',
      Icon: GraduationCap,
      titel: 'Der Doktorhut schaltet den Tutorial-Modus',
      text: 'Hier blendest Du die Erklärkästen aus, wenn Du sie nicht mehr brauchst — und startest diesen Rundgang jederzeit neu.',
      ziel: 'kopf-doktorhut',
      bevorzugt: LINKS,
    },
    {
      id: 'melden',
      Icon: MessageSquarePlus,
      titel: 'Fehlt etwas? Sag es uns',
      text: 'Über „Feedback" meldest Du von jeder Seite aus, was fehlt oder hakt. Ein, zwei Sätze reichen — ein Foto der Seite, der Link und die technischen Angaben gehen automatisch als Ticket mit.',
      ziel: 'kopf-melden',
      bevorzugt: LINKS,
    },
    {
      id: 'erklaerkasten',
      Icon: FileText,
      titel: 'Jede Seite erklärt sich selbst',
      text: 'Der Kasten ganz oben sagt Dir auf jeder Seite, wofür sie da ist, was Du dort tust und worauf Du achten musst. Darin steckt auch „Zeig mir die Seite" — eine kurze Tour durch genau diese Seite.',
      ziel: 'seitenhilfe',
      bevorzugt: UNTEN,
    },
    {
      id: 'ablauf',
      Icon: CheckCircle2,
      titel: 'So läuft eine Mail',
      text: 'Vorbereiten, einreichen, die Redaktion prüft und gibt frei, dann geht die Mail zum Wunschtermin raus. Bei Unklarheiten fragt die Redaktion nach — Du bekommst dazu eine E-Mail. Viel Erfolg mit der ersten Mail!',
    },
  ],

  // ------------------------------------------------------------ Startseite
  start: [
    {
      id: 'kacheln',
      Icon: LayoutGrid,
      titel: 'Die Abkürzungen',
      text: 'Die rote Kachel startet direkt eine neue Mail. Daneben Deine Mails, die Vorlagen und die Anleitung.',
      ziel: 'start-kacheln',
      bevorzugt: UNTEN,
    },
    {
      id: 'wartet',
      Icon: Hourglass,
      titel: 'Was wartet worauf',
      text: 'Was bei Dir liegt (Entwürfe und Rückfragen), was bei der Redaktion wartet und was schon geplant ist. So siehst Du sofort, wo etwas hängt.',
      ziel: 'start-wartet',
      bevorzugt: UNTEN,
    },
    {
      id: 'liste',
      Icon: Inbox,
      titel: 'Deine letzten Mails',
      text: 'Mit Stand und Wunschtermin. Ein Klick öffnet die Mail mit Vorschau und Verlauf.',
      ziel: 'start-liste',
      bevorzugt: ['oben', 'unten'],
    },
  ],

  // -------------------------------------------------------------- Dashboard
  dashboard: [
    {
      id: 'kpis',
      Icon: LayoutGrid,
      titel: 'Die Zahlen',
      text: 'Kampagnen, versendete Mails, Gruppen und Kontakte mit Einwilligung.',
      ziel: 'dash-kpis',
      bevorzugt: UNTEN,
    },
    {
      id: 'freigaben',
      Icon: ClipboardCheck,
      titel: 'Was auf die Redaktion wartet',
      text: 'Eingereichte Mails von Geschäftsführung und Studioleitung. Ein Klick führt zu den Freigaben. Wartet nichts, fehlt der Hinweis.',
      ziel: 'dash-freigaben',
      rolle: 'redaktion',
      bevorzugt: UNTEN,
    },
    {
      id: 'liste',
      Icon: Mail,
      titel: 'Die letzten Kampagnen',
      text: 'Mit Stand und Versandtermin. Ein Klick öffnet die Kampagne im Editor.',
      ziel: 'dash-liste',
      bevorzugt: ['oben', 'unten'],
    },
  ],

  // -------------------------------------------------------------- Assistent
  assistent: [
    {
      id: 'schritte',
      Icon: ListChecks,
      titel: 'Die Schritte',
      text: 'Oben siehst Du, wo Du gerade bist. Erledigte Schritte haben einen Haken; mit einem Klick springst Du zurück. Nach vorne geht es über „Weiter".',
      ziel: 'assistent-schritte',
      bevorzugt: UNTEN,
    },
    {
      id: 'begleiter',
      Icon: Sparkles,
      titel: 'Was jetzt zu tun ist',
      text: 'Jeder Schritt sagt Dir oben, was Du hier tust und warum. Bei den Vorab-Fragen steht unter jeder Frage, welche Abhängigkeit dahintersteckt.',
      ziel: 'assistent-begleiter',
      bevorzugt: UNTEN,
    },
    {
      id: 'arbeit',
      Icon: FileText,
      titel: 'Hier arbeitest Du',
      text: 'Vorlage wählen, Fragen beantworten, Felder ausfüllen, Empfänger wählen — je nach Schritt. Pflichtfelder sind markiert; fehlt etwas, sagt Dir der Assistent genau was.',
      ziel: 'assistent-arbeit',
      bevorzugt: ['rechts', 'unten', 'oben'],
    },
    {
      id: 'vorlauf',
      Icon: Hourglass,
      titel: 'Vorlauf und „eilt"',
      text: 'Die Redaktion sichtet montags bis freitags und braucht vier volle Werktage — am Montag eingereicht, geht die Mail frühestens am Freitag raus. Ist es knapper, markierst Du die Mail bei den Vorab-Fragen als „eilt" und begründest es. Was bis 10 Uhr da ist, geht noch am selben Tag raus, sonst am nächsten Werktag.',
      ziel: 'assistent-vorlauf',
      bevorzugt: RECHTS,
    },
    {
      id: 'gestaltung',
      Icon: Sparkles,
      titel: 'Text und Gestaltung',
      text: 'Im Schritt „Inhalt" formatierst Du den Text mit den Knöpfen über dem Feld: fett, kursiv, markiert, Farbe und Größe. Unter „Gestaltung" fügst Du ein Bannerbild ein (verschieben und vergrößern im Zuschnitt), wählst das Symbol im Kopf und wie viele Emojis die Mail bekommt.',
      ziel: 'assistent-gestaltung',
      bevorzugt: RECHTS,
    },
    {
      id: 'vorschau',
      Icon: Eye,
      titel: 'Die Vorschau',
      text: 'Im Schritt „Inhalt" siehst Du rechts die fertige Mail, genau so, wie sie ankommt — umschaltbar zwischen Computer und Handy.',
      ziel: 'assistent-vorschau',
      bevorzugt: LINKS,
    },
    {
      id: 'weiter',
      Icon: Save,
      titel: 'Weiter heißt auch: gespeichert',
      text: '„Weiter" speichert den Entwurf. Du kannst jederzeit aufhören und später unter „Meine Mails" weitermachen.',
      ziel: 'assistent-weiter',
      bevorzugt: ['oben', 'links'],
    },
    {
      id: 'einreichen',
      Icon: Send,
      titel: 'Zum Schluss: prüfen und einreichen',
      text: 'Im letzten Schritt prüft der Assistent alles noch einmal, Du schickst Dir eine Testmail und schreibst der Redaktion, was sie wissen muss. Offene Punkte halten die Mail auf — bis sie geklärt sind.',
    },
    {
      id: 'versand',
      Icon: ShieldCheck,
      titel: 'Wenn Du ohne Freigabe versenden darfst',
      text: 'Hat Dir die Redaktion das Recht gegeben, wählst Du hier, wann die Mail rausgeht, und bestätigst den letzten Check vor dem Versand. Zur Freigabe geben kannst Du sie trotzdem jederzeit.',
      ziel: 'assistent-versand',
      bevorzugt: RECHTS,
    },
  ],

  // ------------------------------------------------------------ Meine Mails
  'meine-mails': [
    {
      id: 'filter',
      Icon: Inbox,
      titel: 'Nach Stand filtern',
      text: '„Bei mir" sind Entwürfe und Rückfragen — hier bist Du dran. „Bei der Redaktion" wartet auf die Prüfung, „Erledigt" ist geplant, versendet oder abgelehnt.',
      ziel: 'meine-filter',
      bevorzugt: UNTEN,
    },
    {
      id: 'liste',
      Icon: Mail,
      titel: 'Deine Mails',
      text: 'Mit Stand, Wunschtermin und wer gerade dran ist. Ein Klick öffnet die Mail rechts daneben.',
      ziel: 'meine-liste',
      bevorzugt: RECHTS,
    },
    {
      id: 'detail',
      Icon: Eye,
      titel: 'Die Mail im Detail',
      text: 'Vorschau, Empfänger, Rückfrage der Redaktion und die Knöpfe, die gerade passen: bearbeiten, zurückziehen, löschen.',
      ziel: 'meine-detail',
      bevorzugt: LINKS,
    },
    {
      id: 'verlauf',
      Icon: History,
      titel: 'Der Verlauf',
      text: 'Jeder Schritt mit Datum und Person: angelegt, eingereicht, in Prüfung, Rückfrage, freigegeben, versendet.',
      ziel: 'meine-verlauf',
      bevorzugt: LINKS,
    },
  ],

  // -------------------------------------------------------------- Freigaben
  freigaben: [
    {
      id: 'reiter',
      Icon: ClipboardCheck,
      titel: 'Drei Reiter',
      text: '„Offen" ist die Arbeitsliste. „Entschieden" zeigt die letzten Entscheidungen, das „Protokoll" jeden Schritt aller Mails.',
      ziel: 'freigaben-reiter',
      bevorzugt: UNTEN,
    },
    {
      id: 'liste',
      Icon: Hourglass,
      titel: 'Was wartet',
      text: 'Eilige Mails stehen oben, danach nach Wunschtermin. Dahinter steht, wer eingereicht hat und seit wann die Mail wartet.',
      ziel: 'freigaben-liste',
      bevorzugt: RECHTS,
    },
    {
      id: 'detail',
      Icon: Eye,
      titel: 'Prüfen',
      text: 'Vorschau, Betreff, Absender, Antworten-an, Empfängergruppen mit Zahl und die internen Verteiler. Dazu die Antworten auf die Vorab-Fragen und die Nachricht an die Redaktion.',
      ziel: 'freigaben-detail',
      bevorzugt: LINKS,
    },
    {
      id: 'entscheiden',
      Icon: CheckCircle2,
      titel: 'Entscheiden',
      text: '„Freigeben" plant den Versand zum Wunschtermin — oder sofort bzw. zu einem anderen Termin. „Rückfrage" schickt die Mail mit Deiner Frage zurück, „Ablehnen" legt sie mit Begründung ab. Die Person bekommt jeweils eine E-Mail.',
      ziel: 'freigaben-entscheiden',
      bevorzugt: ['oben', 'links', 'unten'],
    },
    {
      id: 'vieraugen',
      Icon: Users,
      titel: 'Vier Augen',
      text: 'Die eigene Mail gibt immer jemand anderes aus der Redaktion frei. Wer das Freigaberecht hat, steht unter Einstellungen → Benutzer.',
    },
    {
      id: 'protokoll',
      Icon: MessageSquare,
      titel: 'Das Protokoll',
      text: 'Jeder Schritt jeder Mail mit Zeit, Person und Begründung — unveränderlich, auch wenn eine Mail später gelöscht wird.',
      ziel: 'freigaben-protokoll',
      seite: '/freigaben?reiter=protokoll',
      bevorzugt: UNTEN,
    },
  ],

  // --------------------------------------------------------------- Vorlagen
  vorlagen: [
    {
      id: 'galerie',
      Icon: LayoutTemplate,
      titel: 'Die Vorlagen',
      text: 'Jede Karte sagt, wofür die Vorlage da ist und wann Du sie nimmst — und nach welchen Mails sie gebaut ist.',
      ziel: 'vorlagen-galerie',
      bevorzugt: RECHTS,
    },
    {
      id: 'vorschau',
      Icon: Eye,
      titel: 'So sieht sie aus',
      text: 'Die Vorschau zeigt die Vorlage mit Beispieltext. Darunter stehen die Fragen, die der Assistent vorab stellt.',
      ziel: 'vorlagen-vorschau',
      bevorzugt: LINKS,
    },
    {
      id: 'emojis',
      Icon: Sparkles,
      titel: 'Wie viele Emojis?',
      text: 'Jede Vorlage gibt es ohne Emojis, normal (ein Symbol im Kopf, 👋 im Gruß, 🥋 in der Signatur) oder mit vielen, passend zum Thema. Im Assistenten wählst Du das unter „Gestaltung".',
      ziel: 'vorlagen-emojis',
      bevorzugt: UNTEN,
    },
    {
      id: 'nutzen',
      Icon: Layers,
      titel: 'Loslegen',
      text: '„Diese Vorlage nutzen" öffnet den Assistenten mit genau dieser Vorlage.',
      ziel: 'vorlagen-nutzen',
      rolle: 'vorbereiten',
      bevorzugt: UNTEN,
    },
  ],

  // ---------------------------------------------------------- HTML-Vorlagen
  templates: [
    {
      id: 'galerie',
      Icon: LayoutTemplate,
      titel: 'Die HTML-Vorlagen',
      text: 'Oben die Haus-Vorlagen im Randori-Design (Lehrgang, Gürtelprüfung, Event, Camp, Newsletter), darunter eigene Vorlagen. Ein Klick zeigt rechts die Vorschau.',
      ziel: 'templates-galerie',
      bevorzugt: RECHTS,
    },
    {
      id: 'emojis',
      Icon: Sparkles,
      titel: 'Drei Emoji-Stufen',
      text: 'Jede Haus-Vorlage gibt es ohne Emojis, normal oder mit vielen passend zum Thema. Dieselbe Wahl hast Du im Kampagnen-Editor, wenn Du die Vorlage nimmst.',
      ziel: 'templates-emojis',
      bevorzugt: UNTEN,
    },
    {
      id: 'vorschau',
      Icon: Eye,
      titel: 'Platzhalter ersetzen',
      text: 'Alles in [ECKIGEN KLAMMERN] ist ein Platzhalter. Im Kampagnen-Editor tauschst Du ihn direkt in der Mail aus; der Versand-Check lässt die Mail erst raus, wenn keiner mehr übrig ist.',
      ziel: 'templates-vorschau',
      bevorzugt: LINKS,
    },
    {
      id: 'haus',
      Icon: Layers,
      titel: 'Auf dem neuesten Stand',
      text: '„Haus-Vorlagen aktualisieren" setzt die Haus-Vorlagen auf den Stand des Mailtools. Eigene Vorlagen und fertige Kampagnen bleiben, wie sie sind.',
      ziel: 'templates-haus',
      rolle: 'admin',
      bevorzugt: UNTEN,
    },
  ],

  // ------------------------------------------------------------ Einstellungen
  settings: [
    {
      id: 'reiter',
      Icon: LayoutGrid,
      titel: 'Die Reiter',
      text: '„Benutzer & Rechte" zeigt, wer das Mailtool nutzt und was er darf. Dazu kommen Freigabe-Workflow, Absender und Sync-Status.',
      ziel: 'settings-reiter',
      bevorzugt: UNTEN,
    },
    {
      id: 'zahlen',
      Icon: Users,
      titel: 'Wer ist dabei?',
      text: 'Die Zahlen je Rolle, dazu wer freigibt und wer ohne Freigabe versenden darf. Ein Klick auf eine Zahl filtert die Tabelle.',
      ziel: 'rechte-zahlen',
      bevorzugt: UNTEN,
    },
    {
      id: 'matrix',
      Icon: ShieldCheck,
      titel: 'Wer darf was?',
      text: 'Die Aufschlüsselung zeigt für jede Rolle, was sie im Mailtool darf — vom Vorbereiten über das Freigeben bis zum Versand ohne Freigabe.',
      ziel: 'rechte-matrix',
      bevorzugt: UNTEN,
    },
    {
      id: 'tabelle',
      Icon: ListChecks,
      titel: 'Rechte direkt ändern',
      text: 'In der Tabelle änderst Du Rolle und Sportschulen, vergibst das Freigaberecht oder erlaubst den Versand ohne Freigabe. „CRM-Rolle" setzt jemanden wieder auf den Stand aus dem CRM.',
      ziel: 'rechte-tabelle',
      bevorzugt: ['oben', 'unten'],
    },
    {
      id: 'hinzufuegen',
      Icon: MailPlus,
      titel: 'Jemanden eintragen',
      text: '„Person hinzufügen" findet Leute über Name oder E-Mail — sobald sie sich einmal mit Google angemeldet haben.',
      ziel: 'rechte-hinzufuegen',
      bevorzugt: LINKS,
    },
  ],
};

export function schritteFuer(tourId: string, rechte: TourRechte): TourSchritt[] {
  const alle = TOUREN[tourId] ?? [];
  return alle.filter((s) => !s.rolle || rechte[s.rolle]);
}
